// src/components/ClientFormFields.js
// Componente com campos compartilhados entre formularios ADD e EDIT de cliente

import React, { useState } from 'react';
import { Row, Col, Form, Button, ListGroup } from 'react-bootstrap';
import AddressAutocomplete from './AddressAutocomplete.js';

/**
 * Campos do formulario de cliente
 * Usado tanto no formulario de adicionar quanto no modal de editar
 *
 * @param {Object} props
 * @param {Object} props.formData - Dados do formulario (do useClientForm)
 * @param {function} props.onFieldChange - Callback para atualizar campo (fieldName, value)
 * @param {function} props.onNestedFieldChange - Callback para atualizar campo aninhado (parent, child, value)
 * @param {boolean} props.isAdmin - Se usuario eh admin (mostra campos extras)
 * @param {Object} props.addressService - Google AutocompleteService
 * @param {Object} props.nzBounds - LatLngBounds para NZ
 * @param {string} props.language - Idioma ('pt' ou 'en')
 * @param {boolean} props.compact - Se deve usar layout compacto (para modal)
 * @param {boolean} props.showRestrictedTimes - Se deve mostrar campo de horarios restritos
 * @param {boolean} props.showNotes - Se deve mostrar campo de notas
 * @param {boolean} props.showChecklist - Se deve mostrar campo de checklist
 * @param {function} props.onAddChecklistItem - Callback para adicionar item ao checklist
 * @param {function} props.onRemoveChecklistItem - Callback para remover item do checklist
 */
export default function ClientFormFields({
  formData,
  onFieldChange,
  onNestedFieldChange,
  isAdmin = false,
  addressService,
  nzBounds,
  language = 'pt',
  compact = false,
  showRestrictedTimes = true,
  showNotes = true,
  showChecklist = false,
  onAddChecklistItem,
  onRemoveChecklistItem
}) {
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const t = (pt, en) => language === 'pt' ? pt : en;

  // Tamanhos das colunas baseado no modo
  const colSizes = compact
    ? { name: 12, address: 12, time: 6, email: 6, phone: 6, amount: 6, password: 6, notes: 12 }
    : { name: 3, address: 4, time: 2, email: 3, phone: 2, amount: 2, password: 2, notes: 12 };

  return (
    <>
      {/* Linha 1: Nome, Endereco, Tempo */}
      <Row className="g-2 mb-2">
        {/* Nome */}
        <Col md={colSizes.name}>
          <Form.Label>{t('Nome', 'Name')} <span className="text-danger">*</span></Form.Label>
          <Form.Control
            type="text"
            value={formData.name}
            onChange={(e) => onFieldChange('name', e.target.value)}
            placeholder={t('Nome do cliente', 'Client name')}
            required
          />
        </Col>

        {/* Endereco com autocomplete */}
        <Col md={colSizes.address}>
          <AddressAutocomplete
            value={formData.address}
            onChange={(value) => onFieldChange('address', value)}
            addressService={addressService}
            nzBounds={nzBounds}
            label={<>{t('Endereço', 'Address')} <span className="text-danger">*</span></>}
            placeholder={t('Digite o endereço...', 'Enter address...')}
            required
          />
        </Col>

        {/* Tempo */}
        <Col md={colSizes.time}>
          <Form.Label>{t('Tempo', 'Time')} <span className="text-danger">*</span></Form.Label>
          <Form.Control
            type="text"
            value={formData.hours}
            onChange={(e) => onFieldChange('hours', e.target.value)}
            placeholder="HH:MM"
            required
          />
        </Col>
      </Row>

      {/* Linha 2: Email, Telefone, Senha */}
      <Row className="g-2 mb-2">
        {/* Email */}
        <Col md={colSizes.email}>
          <Form.Label>
            Email
            {formData.paymentMethod === 'xero' && <span className="text-danger"> *</span>}
          </Form.Label>
          <Form.Control
            type="email"
            value={formData.email}
            onChange={(e) => onFieldChange('email', e.target.value)}
            placeholder={t('Email do cliente', 'Client email')}
            required={formData.paymentMethod === 'xero'}
          />
        </Col>

        {/* Telefone */}
        <Col md={colSizes.phone}>
          <Form.Label>{t('Telefone', 'Phone')}</Form.Label>
          <Form.Control
            type="text"
            value={formData.phone}
            onChange={(e) => onFieldChange('phone', e.target.value)}
            placeholder={t('Telefone', 'Phone')}
          />
        </Col>

        {/* Senha/Codigo */}
        <Col md={colSizes.password}>
          <Form.Label>{t('Senha/Código', 'Password/Code')}</Form.Label>
          <Form.Control
            type="text"
            value={formData.password}
            onChange={(e) => onFieldChange('password', e.target.value)}
            placeholder={t('Código de acesso', 'Access code')}
          />
        </Col>
      </Row>

      {/* Linha 3: Campos de Admin (Valor e Metodo de Pagamento) */}
      {isAdmin && (
        <Row className="g-2 mb-2">
          {/* Valor Padrao */}
          <Col md={colSizes.amount}>
            <Form.Label>{t('Valor Padrão ($)', 'Default Amount ($)')}</Form.Label>
            <Form.Control
              type="number"
              step="0.01"
              min="0"
              value={formData.defaultAmount}
              onChange={(e) => onFieldChange('defaultAmount', e.target.value)}
              placeholder="0.00"
            />
          </Col>

          {/* Metodo de Pagamento */}
          <Col md={compact ? 6 : 4}>
            <Form.Label>{t('Método de Pagamento', 'Payment Method')}</Form.Label>
            <div className="d-flex gap-3 mt-1">
              <Form.Check
                type="radio"
                id={`payment-cash-${compact ? 'edit' : 'add'}`}
                name={`paymentMethod-${compact ? 'edit' : 'add'}`}
                label="Cash"
                checked={formData.paymentMethod === 'cash'}
                onChange={() => onFieldChange('paymentMethod', 'cash')}
              />
              <Form.Check
                type="radio"
                id={`payment-xero-${compact ? 'edit' : 'add'}`}
                name={`paymentMethod-${compact ? 'edit' : 'add'}`}
                label="Xero"
                checked={formData.paymentMethod === 'xero'}
                onChange={() => onFieldChange('paymentMethod', 'xero')}
              />
            </div>
          </Col>
        </Row>
      )}

      {/* Linha 4: Notas (opcional) */}
      {showNotes && (
        <Row className="g-2 mb-2">
          <Col md={colSizes.notes}>
            <Form.Label>{t('Notas Padrão', 'Default Notes')}</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={formData.notes}
              onChange={(e) => onFieldChange('notes', e.target.value)}
              placeholder={t('Notas sobre o cliente...', 'Notes about client...')}
            />
          </Col>
        </Row>
      )}

      {/* Linha 5: Horarios Restritos (opcional) */}
      {showRestrictedTimes && (
        <Row className="g-2 mb-2">
          <Col md={12}>
            <Form.Check
              type="checkbox"
              id={`restricted-times-${compact ? 'edit' : 'add'}`}
              label={t('Restringir horários permitidos', 'Restrict allowed times')}
              checked={formData.restrictedTimes?.enabled || false}
              onChange={(e) => onNestedFieldChange('restrictedTimes', 'enabled', e.target.checked)}
            />

            {formData.restrictedTimes?.enabled && (
              <Row className="g-2 mt-2">
                <Col md={compact ? 6 : 3}>
                  <Form.Label>{t('Início permitido', 'Allowed start')}</Form.Label>
                  <Form.Control
                    type="time"
                    value={formData.restrictedTimes?.allowedStart || ''}
                    onChange={(e) => onNestedFieldChange('restrictedTimes', 'allowedStart', e.target.value)}
                  />
                </Col>
                <Col md={compact ? 6 : 3}>
                  <Form.Label>{t('Fim permitido', 'Allowed end')}</Form.Label>
                  <Form.Control
                    type="time"
                    value={formData.restrictedTimes?.allowedEnd || ''}
                    onChange={(e) => onNestedFieldChange('restrictedTimes', 'allowedEnd', e.target.value)}
                  />
                </Col>
                <Col md={compact ? 12 : 6}>
                  <Form.Text className="text-muted">
                    {t(
                      'O serviço só poderá ser agendado dentro deste intervalo de horário.',
                      'Service can only be scheduled within this time range.'
                    )}
                  </Form.Text>
                </Col>
              </Row>
            )}
          </Col>
        </Row>
      )}

      {/* Linha 6: Checklist (opcional) */}
      {showChecklist && (
        <Row className="g-2 mb-2">
          <Col md={12}>
            <Form.Label>{t('Checklist Padrão', 'Default Checklist')}</Form.Label>

            {/* Lista de itens existentes */}
            {formData.checklist && formData.checklist.length > 0 && (
              <ListGroup className="mb-2">
                {formData.checklist.map((item, index) => {
                  // Support both formats: string or {text, done} object
                  const itemText = typeof item === 'string' ? item : item?.text || '';
                  return (
                    <ListGroup.Item
                      key={index}
                      className="d-flex justify-content-between align-items-center py-2"
                    >
                      <span>✓ {itemText}</span>
                      {onRemoveChecklistItem && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => onRemoveChecklistItem(index)}
                          style={{ padding: '2px 8px', fontSize: '0.8rem' }}
                        >
                          ✕
                        </Button>
                      )}
                    </ListGroup.Item>
                  );
                })}
              </ListGroup>
            )}

            {/* Input para adicionar novo item */}
            {onAddChecklistItem && (
              <div className="d-flex gap-2">
                <Form.Control
                  type="text"
                  value={newChecklistItem}
                  onChange={(e) => setNewChecklistItem(e.target.value)}
                  placeholder={t('Adicionar item ao checklist...', 'Add checklist item...')}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && newChecklistItem.trim()) {
                      e.preventDefault();
                      onAddChecklistItem(newChecklistItem.trim());
                      setNewChecklistItem('');
                    }
                  }}
                />
                <Button
                  variant="outline-primary"
                  onClick={() => {
                    if (newChecklistItem.trim()) {
                      onAddChecklistItem(newChecklistItem.trim());
                      setNewChecklistItem('');
                    }
                  }}
                  disabled={!newChecklistItem.trim()}
                >
                  +
                </Button>
              </div>
            )}

            {(!formData.checklist || formData.checklist.length === 0) && (
              <Form.Text className="text-muted">
                {t('Nenhum item no checklist', 'No checklist items')}
              </Form.Text>
            )}
          </Col>
        </Row>
      )}
    </>
  );
}

/**
 * Versao simplificada apenas com campos essenciais
 */
export function ClientFormFieldsBasic({
  formData,
  onFieldChange,
  addressService,
  nzBounds,
  language = 'pt'
}) {
  return (
    <ClientFormFields
      formData={formData}
      onFieldChange={onFieldChange}
      onNestedFieldChange={() => {}}
      addressService={addressService}
      nzBounds={nzBounds}
      language={language}
      isAdmin={false}
      showRestrictedTimes={false}
      showNotes={false}
      compact={true}
    />
  );
}
